<template>
    <div class="date-range-picker-container">
        <template v-if="isRange">
            <div class="date-range-picker-zone">
                <div class="date-range-picker-header date-range-picker-header-start">
                    <button
                        type="button"
                        class="date-range-picker-icon-btn el-icon-d-arrow-left"
                        @click="handleYearChange(true, true)"
                    />

                    <div>{{ leftLabel }}</div>

                    <button
                        :disabled="!enableBtn"
                        :class="[
                            'date-range-picker-icon-btn',
                            'el-icon-d-arrow-right',
                            !enableBtn ? 'is-disabled' : ''
                        ]"
                        type="button"
                        @click="handleYearChange(true)"
                    />
                </div>

                <div class="date-range-picker-table-wrapper">
                    <button
                        :class="[
                            'date-range-picker-icon-btn ',
                            'el-icon-arrow-left',
                            showPageBtns.leftPrev ? 'is-show' : ''
                        ]"
                        type="button"
                        @click="handlePageChange(true, true)"
                    />

                    <DateTable
                        key="start"
                        :year="leftDate"
                        :page="leftPage"
                        :rows.sync="leftRows"
                        :range-state.sync="rangeState"
                        :min-date.sync="minDate"
                        :max-date.sync="maxDate"
                        :page-count="pageCount"
                        :single-row-max-col="singleRowMaxCol"
                        :start-limit-date="startLimitDate"
                        :end-limit-date="endLimitDate"
                        :is-range="true"
                        class="date-range-picker-table"
                    />

                    <button
                        :disabled="!enablePageBtn"
                        :class="[
                            'date-range-picker-icon-btn ',
                            'el-icon-arrow-right',
                            showPageBtns.leftNext ? 'is-show' : ''
                        ]"
                        type="button"
                        @click="handlePageChange(true)"
                    />
                </div>
            </div>

            <div class="date-range-picker-zone">
                <div class="date-range-picker-header date-range-picker-header-end">
                    <button
                        :disabled="!enableBtn"
                        :class="[
                            'date-range-picker-icon-btn',
                            'el-icon-d-arrow-left',
                            !enableBtn ? 'is-disabled' : '',
                        ]"
                        type="button"
                        @click="handleYearChange(false, true)"
                    />

                    <div>{{ rightLabel }}</div>

                    <button
                        type="button"
                        class="date-range-picker-icon-btn el-icon-d-arrow-right"
                        @click="handleYearChange(false)"
                    />
                </div>
                <div class="date-range-picker-table-wrapper">
                    <button
                        :disabled="!enablePageBtn"
                        :class="[
                            'date-range-picker-icon-btn',
                            'el-icon-arrow-left',
                            showPageBtns.rightPrev ? 'is-show' : ''
                        ]"
                        type="button"
                        @click="handlePageChange(false, true)"
                    />

                    <DateTable
                        key="end"
                        :year="rightDate"
                        :page="rightPage"
                        :rows.sync="rightRows"
                        :range-state.sync="rangeState"
                        :min-date.sync="minDate"
                        :max-date.sync="maxDate"
                        :page-count="pageCount"
                        :single-row-max-col="singleRowMaxCol"
                        :start-limit-date="startLimitDate"
                        :end-limit-date="endLimitDate"
                        :is-range="true"
                        class="date-range-picker-table"
                    />

                    <button
                        :class="[
                            'date-range-picker-icon-btn',
                            'el-icon-arrow-right',
                            showPageBtns.rightNext ? 'is-show' : ''
                        ]"
                        type="button"
                        @click="handlePageChange(false)"
                    />
                </div>
            </div>
        </template>

        <div v-else class="date-range-picker-zone">
            <div class="date-range-picker-header date-range-picker-header-end">
                <button
                    :class="[
                        'date-range-picker-icon-btn',
                        'el-icon-d-arrow-left',
                    ]"
                    type="button"
                    @click="handleYearChange(true, true)"
                />

                <div>{{ yearLabel }}</div>

                <button
                    type="button"
                    class="date-range-picker-icon-btn el-icon-d-arrow-right"
                    @click="handleYearChange(true)"
                />
            </div>
            <div class="date-range-picker-table-wrapper">
                <button
                    :disabled="!enablePageBtn"
                    :class="[
                        'date-range-picker-icon-btn',
                        'el-icon-arrow-left',
                        showPageBtns.prev ? 'is-show' : ''
                    ]"
                    type="button"
                    @click="handlePageChange(true, true)"
                />

                <DateTable
                    :year="year"
                    :page="page"
                    :rows.sync="rows"
                    :range-state.sync="rangeState"
                    :date.sync="date"
                    :page-count="pageCount"
                    :single-row-max-col="singleRowMaxCol"
                    :start-limit-date="startLimitDate"
                    :end-limit-date="endLimitDate"
                    :is-range="false"
                    class="date-range-picker-table"
                />

                <button
                    :class="[
                        'date-range-picker-icon-btn',
                        'el-icon-arrow-right',
                        showPageBtns.next ? 'is-show' : ''
                    ]"
                    type="button"
                    @click="handlePageChange(true)"
                />
            </div>
        </div>
    </div>
</template>


<script>
import DateTable from "./DateTable";

import {
  RANGE_TYPE_WEEK,
  RANGE_TYPE_MONTH,
  RANGE_TYPE_QUARTER,
  RANGE_TYPE_HALF_YEAR,
  RANGE_TYPE_YEAR,
  TYPE_MONTH,
  TYPE_YEAR,
  TYPE_WEEK,
  TYPE_QUARTER
} from "./enums";

import Methods from "./methods";

const valueEquals = function (a, b) {
  // considers Date object and string
  const dateEquals = function (a, b) {
    const aIsDate = a instanceof Date;
    const bIsDate = b instanceof Date;
    
    if (aIsDate && bIsDate) {
      return a.getTime() === b.getTime();
    }

    if (!aIsDate && !bIsDate) {
      return a === b;
    }

    return false;
  };

  const aIsArray = a instanceof Array;
  const bIsArray = b instanceof Array;

  if (aIsArray && bIsArray) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => dateEquals(item, b[index]));
  }
  if (!aIsArray && !bIsArray) {
    return dateEquals(a, b);
  }
  return false;
};

export default {
  name: "DateRangePop",

  components: {
    DateTable
  },

  props: {
    type: {
      default: RANGE_TYPE_MONTH,
      type:
      RANGE_TYPE_WEEK |
      RANGE_TYPE_MONTH |
      RANGE_TYPE_QUARTER |
      RANGE_TYPE_HALF_YEAR |
      RANGE_TYPE_YEAR |
      TYPE_MONTH |
      TYPE_YEAR |
      TYPE_WEEK |
      TYPE_QUARTER
    },

    value: {
      default: null,
      type: Array | String
    },

    visible: {
      default: false,
      type: Boolean
    },

    limit: {
      default: () => [],
      type: Array
    },

    isRange: {
      default: false,
      type: Boolean
    },

    isStart: {
      default: true,
      type: Boolean
    }
  },

  data() {
    const date = new Date();
    const currYear = date.getFullYear();

    return {
      rangeState: {
        endDate: null,
        selecting: false,
        row: null,
        year: null,
        page: 1,
        column: null
      },

      maxDate: null,
      minDate: null,

      leftRows: [],
      rightRows: [],

      leftDate: currYear,
      rightDate: currYear + 1,

      leftPage: 1,
      rightPage: 1,

      date: null,
      rows: [],
      year: currYear,
      page: 1
    };
  },

  computed: {
    leftLabel() {
      return Methods[this.type].displayLabelFormatter(this.leftDate);
    },

    rightLabel() {
      return Methods[this.type].displayLabelFormatter(this.rightDate);
    },

    yearLabel() {
      return Methods[this.type].displayLabelFormatter(this.year);
    },

    enableBtn() {
      return Methods[this.type].enableBtn(this.leftDate, this.rightDate);
    },

    enablePageBtn() {
      return Methods[this.type].enablePageBtn(
        this.leftDate,
        this.rightDate,
        this.leftPage,
        this.rightPage
      );
    },

    startLimitDate() {
      return this.limit[0] ? Methods[this.type].dateToPos(this.limit[0]) : null;
    },

    endLimitDate() {
      return this.limit[1] ? Methods[this.type].dateToPos(this.limit[1]) : null;
    },

    singleRowMaxCol() {
      return Methods[this.type].maxColumn;
    },

    pageCount() {
      const { maxColumn, maxRow } = Methods[this.type];
      return maxColumn * maxRow;
    },

    leftRowsLen() {
      return this.leftRows.length;
    },

    rightRowsLen() {
      return this.rightRows.length;
    },

    rowsLen() {
      return this.rows.length;
    },

    showPageBtns() {
      if (this.type === RANGE_TYPE_WEEK) {
        return {
          leftPrev: this.leftPage > 1,
          leftNext: this.leftPage < this.leftRowsLen,
          rightPrev: this.rightPage > 1,
          rightNext: this.rightPage < this.rightRowsLen
        }; 
      } if (this.type === TYPE_WEEK) {
        return {
          prev: this.page > 1,
          next: this.page < this.rowsLen
        };
      } else {
        return {
          leftPrev: false,
          leftNext: false,
          rightPrev: false,
          rightNext: false
        };
      }
    },

    results() {
      if (this.isRange) {
        if (this.minDate && this.maxDate) {
          return [
            Methods[this.type].valueFormatter(this.minDate, true),
            Methods[this.type].valueFormatter(this.maxDate)
          ];
        }

        return [];
      } else {
        if (this.date) {
          return Methods[this.type].valueFormatter(this.date,this.isStart);
        }

        return "";
      }
    }
  },

  watch: {
    results(val) {
      if (this.isRange) {
        if (val.length === 2) {
          this.$emit("update:visible", false);
          this.$emit("input", val);
          this.$emit("change", val);
        }
      } else {
        this.$emit("update:visible", false);
        this.$emit("input", val);
        this.$emit("change", val);
      }
    },

    value(val, oldVal) {
      if (!valueEquals(val, oldVal) && !this.visible) {
        this.init();
      }
    },

    type() {
      this.init();
    }
  },

  mounted() {
    this.init();
  },

  methods: {
    init() {
      const value = this.value;
      const currDate = new Date();
      const currYear = currDate.getFullYear();

      let startYear = currYear,
        endYear = Methods[this.type].rightDate(startYear, currYear),
        leftPage = 1,
        rightPage = 1;

      if (value) {
        if (Array.isArray(value)) {
          let [startDate, endDate] = value;

          if ( startDate && endDate && new Date(startDate).getTime() > new Date(endDate).getTime()) {
            startDate = [endDate, endDate = startDate][0];
          }
          
          if (startDate) {
            const minDate = Methods[this.type].dateToPos(startDate);

            this.leftDate = startYear = Methods[this.type].leftDate(
              minDate.year
            );

            leftPage = minDate.page;

            this.minDate = minDate;
          }

          if (endDate) {
            const maxDate = Methods[this.type].dateToPos(endDate);

            this.rightDate = endYear = Methods[this.type].rightDate(
              startYear,
              maxDate.year,
            );

            if (startYear !== maxDate.year) {
              rightPage = maxDate.page;
            }

            this.maxDate = maxDate;
          } else {
            this.rightDate = endYear = Methods[this.type].rightDate(
              this.leftDate,
              this.rightDate,
            );
          }
        } else {
          const date = Methods[this.type].dateToPos(value);

          this.date = date;
          this.page = date.page;
          this.year = Methods[this.type].leftDate(date.year);
          this.rows = this.initRows(date.year);
        }
      } else {
        this.minDate = null;
        this.maxDate = null;
        this.date = null;

        this.rangeState = {
          endDate: null,
          selecting: false,
          row: null,
          year: null,
          page: 1,
          column: null
        };
      }

      this.leftPage = leftPage;
      this.rightPage = rightPage;

      if (this.isRange) {
        this.leftRows = this.initRows(startYear);
        this.rightRows = this.initRows(endYear);
      } else {
        this.rows = this.initRows(startYear);
      }
    },

    initRows(starter) {
      return Methods[this.type].generator(starter);
    },

    handleYearChange(isLeftAction, isPrev) {
      if (this.isRange) {
        if (isLeftAction) {
          this.leftDate += Methods[this.type].change(isPrev);
          this.leftRows = this.initRows(this.leftDate);
          this.leftPage = isPrev ? this.leftRowsLen : 1;
        } else {
          this.rightDate += Methods[this.type].change(isPrev);
          this.rightRows = this.initRows(this.rightDate);
          this.rightPage = isPrev ? 1 : this.rightRowsLen;
        }
      } else {
        this.year += Methods[this.type].change(isPrev);
        this.rows = this.initRows(this.year);
        this.page = 1;
      }
    },

    handlePageChange(isLeftAction, isPrev) {
      this[this.isRange ? isLeftAction ? "leftPage" : "rightPage" : "page"] += Methods[this.type].pageChange(isPrev);
    }
  }
};
</script>